import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import apiClient from '../api/client';
import { safeInternalPath } from '../utils/safeNext';
import './Login.css';

function Login() {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  // Stable refs so the Google init effect runs once on mount instead of
  // re-running every time AuthContext re-creates loginWithGoogle. Re-running
  // the init causes google.accounts.id.initialize() to fire repeatedly,
  // which aborts the in-flight GSI button iframe and leaves the login page
  // with an empty space where the Google button should render.
  const loginWithGoogleRef = useRef(loginWithGoogle);
  const navigateRef = useRef(navigate);
  useEffect(() => { loginWithGoogleRef.current = loginWithGoogle; }, [loginWithGoogle]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Safe post-login destination: honor a ?next= (set by the auth interceptor when
  // it force-signs-out on a dead session) but ONLY for internal paths — never an
  // absolute or protocol-relative URL (open-redirect guard). Falls back to /dashboard.
  const getSafeNext = () => {
    let n = null;
    try {
      n = new URLSearchParams(window.location.search).get('next');
    } catch (_) { /* ignore */ }
    return safeInternalPath(n, window.location.origin) || '/dashboard';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!emailOrUsername.trim()) {
      setError('Please enter your email or username.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    const result = await login(emailOrUsername, password);
    if (result.success) {
      // Admins without a selected mode go to mode selection
      // ProtectedRoute will handle this, but we can optimize by going there directly
      navigate(getSafeNext());
    } else {
      setError(result.error);
    }
  };

  // Initialize Google Sign-In
  useEffect(() => {
    const initGoogleSignIn = async () => {
      const loginContainer = document.getElementById('googleSignInBtnLogin');
      if (!loginContainer) return;

      // Fetch client ID from backend using apiClient for consistency
      try {
        const response = await apiClient.get('/api/auth/google/client-id');
        const clientId = response.data.client_id;
        
        if (!clientId) {
          // Google OAuth not configured, hide button container
          loginContainer.style.display = 'none';
          const divider = loginContainer.previousElementSibling;
          if (divider && divider.classList.contains('oauth-divider')) {
            divider.style.display = 'none';
          }
          return;
        }

        // Wait for Google Identity Services to load
        const checkGoogleLoaded = () => {
          if (window.google && window.google.accounts && window.google.accounts.id) {
            window.google.accounts.id.initialize({
              client_id: clientId,
              callback: async (resp) => {
                const credential = resp?.credential;
                if (credential) {
                  setError('');
                  const result = await loginWithGoogleRef.current(credential);
                  if (result.success) {
                    navigateRef.current(getSafeNext());
                  } else {
                    setError(result.error);
                  }
                }
              },
            });

            const containerWidth = loginContainer.parentElement?.offsetWidth || 360;
            const buttonWidth = Math.min(360, containerWidth);

            const buttonOptions = {
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              shape: 'pill',
              width: buttonWidth,
            };

            window.google.accounts.id.renderButton(loginContainer, buttonOptions);
            return true;
          }
          return false;
        };

        // Try immediately, then retry if needed
        if (!checkGoogleLoaded()) {
          let tries = 0;
          intervalRef = setInterval(() => {
            tries++;
            if (checkGoogleLoaded() || tries > 25) {
              clearInterval(intervalRef);
              intervalRef = null;
            }
          }, 80);
        }
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
        loginContainer.style.display = 'none';
        const divider = loginContainer.previousElementSibling;
        if (divider && divider.classList.contains('oauth-divider')) {
          divider.style.display = 'none';
        }
      }
    };

    let intervalRef = null;
    initGoogleSignIn();
    return () => { if (intervalRef) clearInterval(intervalRef); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDev =
    process.env.NODE_ENV === 'development' ||
    /^3\d{3}$/.test(window.location.port) ||
    /(^|\.)debono-dev\d*\.phillyshell\.cloud$/i.test(window.location.hostname);

  // These must be users that exist in THIS environment's database. The previous
  // list ('admin'/'admin123' and the owner's pseudonym) was carried over from the
  // owner's prod/preprod and 401s here: this isolated debono_dev seeds a single
  // admin, 'admin1'. Do not re-add owner usernames — they do not exist in this DB
  // and putting the owner's identity in a partner UI breaks owner-anonymity.
  const devAccounts = isDev ? [
    { label: 'admin1', username: 'admin1', password: 'password' },
  ] : [];

  const handleDevLogin = async (acct) => {
    setError('');
    const result = await login(acct.username, acct.password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const [freshBusy, setFreshBusy] = useState(false);
  const handleFreshAccount = async () => {
    if (freshBusy) return;
    setError('');
    setFreshBusy(true);
    const suffix = Math.random().toString(36).slice(2, 8);
    const username = `dev_${suffix}`;
    // fresh.debono.ai — Pydantic email validator rejects reserved TLDs like .local/.test,
    // so the throwaway domain has to be a real-looking subdomain.
    const email = `${username}@fresh.debono.ai`;
    const password = 'dev12345';
    try {
      await apiClient.post('/api/auth/register', {
        email,
        username,
        password,
        referral_code: null,
      });
      const result = await login(username, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Created account but login failed.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 429) {
        setError('Register rate-limited (3/hr). Restart backend or wait.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Fresh account creation failed.');
      }
    } finally {
      setFreshBusy(false);
    }
  };

  return (
    <div className="login-container">
      <Helmet>
        <title>Log In - Debono | The Pharmacy RPG</title>
        <meta name="description" content="Log in to Debono to resume pharmacy quizzes, flashcards, boss fights, and study routes built from your own lectures." />
        <link rel="canonical" href="https://debono.ai/login" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Log In - Debono | The Pharmacy RPG" />
        <meta property="og:description" content="Log in to Debono to resume pharmacy quizzes, flashcards, boss fights, and study routes built from your own lectures." />
        <meta property="og:url" content="https://debono.ai/login" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Debono RX" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Log In - Debono | The Pharmacy RPG" />
        <meta name="twitter:description" content="Log in to Debono to resume pharmacy quizzes, flashcards, boss fights, and study routes built from your own lectures." />
      </Helmet>
      <div className="login-box">
        <div className="login-logo">
          <img src="/favicon-128x128.png?v=7" alt="Debono" className="login-favicon" />
          <span className="login-rx-pill">rx</span>
          <span className="login-debono">DEBONO</span>
        </div>
        <h2>Pharmacy Learning Platform</h2>
        {isDev && (
          <div className="dev-quick-login">
            <div className="dev-quick-label">DEV QUICK LOGIN</div>
            <div className="dev-quick-buttons">
              {devAccounts.map(acct => (
                <button
                  key={acct.username}
                  className="dev-quick-btn"
                  onClick={() => handleDevLogin(acct)}
                >
                  {acct.label}
                </button>
              ))}
              <button
                className="dev-quick-btn dev-quick-btn--fresh"
                onClick={handleFreshAccount}
                disabled={freshBusy}
                aria-label="Register a throwaway dev_* account and log in (3/hr rate limit)"
              >
                {freshBusy ? '...' : '+ Fresh'}
              </button>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email or Username</label>
            <input
              type="text"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              placeholder="Enter your email or username"
              required
              autoComplete="username"
              data-testid="login-email-input"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              data-testid="login-password-input"
            />
          </div>
          {error && <div className="error">{error}</div>}
          <button type="submit" data-testid="login-submit-btn">Login</button>
        </form>
        <div className="oauth-divider"><span>or</span></div>
        <div className="oauth-provider-row">
          <div className="oauth-provider" id="googleSignInBtnLogin"></div>
        </div>
        <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
          <Link to="/forgot-password" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: 13 }}>
            Forgot password?
          </Link>
        </div>
        <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
          <Link to="/signup" style={{ color: '#667eea', textDecoration: 'none' }}>
            Don't have an account? Sign Up
          </Link>
        </div>
        <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: 13 }}>
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
